// Journal-feature components: trade-note drawer + tag chip input.
// Kept in one file because they're used together on the Positions table
// and share Storage / Supabase wiring.

import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Camera, Loader2, X, Plus, Tag as TagIcon } from 'lucide-react';
import { supabase, veritas } from '@/lib/supabase';
import { cn, fmtLocalDateTime } from '@/lib/utils';
import type { Tag, TradeNote } from '@/types/veritas';

const SCREENSHOTS_BUCKET = 'veritas-screenshots';

// ════════════════════════════════════════════════════════════════════════
// TradeNoteDrawer — modal for editing the note + uploading a screenshot
// ════════════════════════════════════════════════════════════════════════
export function TradeNoteDrawer({
  open,
  position,
  existingNote,
  onClose,
  onSaved,
}: {
  open: boolean;
  position: { account_login: number; position_ticket: number; credential_id: string; symbol: string; closed_at: string | null };
  existingNote: TradeNote | undefined;
  onClose: () => void;
  onSaved: (note: TradeNote) => void;
}) {
  const [text, setText] = useState(existingNote?.note ?? '');
  const [path, setPath] = useState(existingNote?.screenshot_path ?? '');
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when drawer opens for a different position
  useEffect(() => {
    setText(existingNote?.note ?? '');
    setPath(existingNote?.screenshot_path ?? '');
    setError(null);
  }, [existingNote, open]);

  // Resolve signed URL whenever the path changes
  useEffect(() => {
    let cancelled = false;
    if (!path) { setSignedUrl(null); return; }
    supabase.storage
      .from(SCREENSHOTS_BUCKET)
      .createSignedUrl(path, 3600)
      .then((r) => {
        if (cancelled) return;
        if (r.data?.signedUrl) setSignedUrl(r.data.signedUrl);
        else setSignedUrl(null);
      });
    return () => { cancelled = true; };
  }, [path]);

  if (!open) return null;

  async function uploadScreenshot(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError(null);
    const session = (await supabase.auth.getSession()).data.session;
    if (!session) { setError('Not signed in'); setUploading(false); return; }

    // Path convention: <auth_user_id>/<account>-<position>-<random>.<ext>
    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
    const rand = Math.random().toString(36).slice(2, 10);
    const newPath = `${session.user.id}/${position.account_login}-${position.position_ticket}-${rand}.${ext}`;

    const { error: upErr } = await supabase.storage
      .from(SCREENSHOTS_BUCKET)
      .upload(newPath, file, { contentType: file.type, upsert: false });
    setUploading(false);
    if (upErr) { setError(upErr.message); return; }

    // Delete previous screenshot if there was one (best-effort)
    if (path && path !== newPath) {
      void supabase.storage.from(SCREENSHOTS_BUCKET).remove([path]);
    }
    setPath(newPath);
  }

  async function removeScreenshot() {
    if (!path) return;
    void supabase.storage.from(SCREENSHOTS_BUCKET).remove([path]);
    setPath('');
  }

  async function save() {
    setBusy(true);
    setError(null);
    const payload = {
      account_login:   position.account_login,
      position_ticket: position.position_ticket,
      credential_id:   position.credential_id,
      note:            text.trim() || null,
      screenshot_path: path || null,
    };
    const r = await veritas
      .from('trade_notes')
      .upsert(payload, { onConflict: 'account_login,position_ticket' })
      .select()
      .maybeSingle();
    setBusy(false);
    if (r.error) { setError(r.error.message); return; }
    if (r.data) onSaved(r.data as TradeNote);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-end bg-black/60 sm:items-center sm:justify-center">
      <div
        className="relative flex h-full w-full flex-col rounded-t-xl border border-line bg-bg-1 shadow-2xl sm:h-auto sm:max-h-[88vh] sm:w-[600px] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line-soft px-5 py-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
              Trade Note
            </div>
            <div className="num text-[14px] font-semibold text-text">
              {position.symbol} · #{position.position_ticket}
            </div>
            <div className="num text-[10.5px] text-text-dim"
                 title={position.closed_at ? `${position.closed_at} (UTC)` : ''}>
              closed {fmtLocalDateTime(position.closed_at, false)}
            </div>
          </div>
          <button onClick={onClose} className="rounded-md border border-line p-2 text-text-mid hover:bg-bg-2 hover:text-text" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="thin-scroll flex-1 overflow-auto px-5 py-4">
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
            Why I took it / what I learned
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Setup, bias, market context, what worked, what didn't…"
            rows={8}
            className="mb-4 w-full resize-y rounded-md border border-line bg-bg-2 px-3 py-2.5 text-[13px] text-text outline-none transition-colors placeholder:text-text-faint focus:border-green"
          />

          <label className="mb-2 block text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
            Screenshot
          </label>
          {signedUrl ? (
            <div className="relative mb-2 overflow-hidden rounded-md border border-line">
              <img src={signedUrl} alt="Trade screenshot" className="block max-h-[420px] w-full object-contain bg-bg-2" />
              <button
                onClick={removeScreenshot}
                className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md bg-bg/80 text-text-mid backdrop-blur hover:text-red"
                aria-label="Remove screenshot"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-line bg-bg-2 px-4 py-6 text-[12px] text-text-dim hover:border-green hover:text-text">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
              <span>{uploading ? 'Uploading…' : 'Click to upload (PNG, JPG, or WebP — max 5 MB)'}</span>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadScreenshot} className="hidden" disabled={uploading} />
            </label>
          )}
          {error && (
            <div className="mt-3 rounded-md border border-red/40 bg-red/10 px-3 py-2 text-[12px] text-red">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-line-soft px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-line px-4 py-2 text-[12px] text-text-mid hover:bg-bg-2 hover:text-text"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="rounded-md bg-green px-4 py-2 text-[12px] font-semibold text-bg hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TagChipList — read-only display for inline use in tables
// ════════════════════════════════════════════════════════════════════════
export function TagChipList({
  tagIds,
  tagsById,
  max = 4,
}: {
  tagIds: Set<string>;
  tagsById: Map<string, Tag>;
  max?: number;
}) {
  const tags = useMemo(() => {
    const out: Tag[] = [];
    for (const id of tagIds) {
      const t = tagsById.get(id);
      if (t) out.push(t);
    }
    return out.slice(0, max);
  }, [tagIds, tagsById, max]);
  const overflow = tagIds.size - tags.length;
  if (tags.length === 0) return <span className="text-text-dim">—</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {tags.map((t) => (
        <span
          key={t.id}
          title={t.category ? `${t.name} (${t.category})` : t.name}
          className="rounded-[3px] px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-eyebrow"
          style={{ backgroundColor: `#${t.color}20`, color: `#${t.color}` }}
        >
          {t.name}
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-[9.5px] text-text-dim">+{overflow}</span>
      )}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TagPicker — popover for editing the tag set on a position
// ════════════════════════════════════════════════════════════════════════
export function TagPicker({
  open,
  position,
  selected,
  allTags,
  onClose,
  onChange,
  onCreatePersonalTag,
}: {
  open: boolean;
  position: { account_login: number; position_ticket: number };
  selected: Set<string>;
  allTags: Tag[];
  onClose: () => void;
  onChange: (next: Set<string>) => void;
  onCreatePersonalTag: (name: string) => Promise<Tag | null>;
}) {
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);

  if (!open) return null;

  const filtered = useFilteredTags(allTags, filter);
  const showCreateRow = filter.trim() && !allTags.some((t) => t.name.toLowerCase() === filter.trim().toLowerCase());

  async function toggle(tagId: string) {
    const next = new Set(selected);
    if (next.has(tagId)) {
      next.delete(tagId);
      await veritas
        .from('trade_tags')
        .delete()
        .eq('account_login', position.account_login)
        .eq('position_ticket', position.position_ticket)
        .eq('tag_id', tagId);
    } else {
      next.add(tagId);
      await veritas
        .from('trade_tags')
        .insert({
          account_login: position.account_login,
          position_ticket: position.position_ticket,
          tag_id: tagId,
        });
    }
    onChange(next);
  }

  async function createAndApply() {
    setCreating(true);
    const tag = await onCreatePersonalTag(filter.trim());
    setCreating(false);
    setFilter('');
    if (tag) await toggle(tag.id);
  }

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/40 sm:items-center"
         onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-t-xl border border-line bg-bg-1 shadow-2xl sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line-soft px-4 py-3">
          <span className="text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
            Tags · #{position.position_ticket}
          </span>
          <button onClick={onClose} className="text-text-mid hover:text-text" aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div className="px-4 py-3">
          <input
            autoFocus
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter or type new tag…"
            className="w-full rounded-md border border-line bg-bg-2 px-3 py-2 text-[13px] text-text outline-none focus:border-green"
          />
        </div>
        <div className="thin-scroll max-h-[40vh] overflow-auto px-2 pb-2">
          {filtered.length === 0 && !showCreateRow && (
            <div className="px-3 py-6 text-center text-[11.5px] text-text-dim">
              No tags match. Type to create a personal one.
            </div>
          )}
          {filtered.map((t) => (
            <button
              key={t.id}
              onClick={() => toggle(t.id)}
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left hover:bg-bg-2"
            >
              <span className="inline-flex items-center gap-2">
                <span
                  className="rounded-[3px] px-1.5 py-px text-[10px] font-semibold uppercase tracking-eyebrow"
                  style={{ backgroundColor: `#${t.color}20`, color: `#${t.color}` }}
                >
                  {t.name}
                </span>
                {t.category && (
                  <span className="text-[9.5px] uppercase tracking-eyebrow text-text-dim">
                    {t.category}
                  </span>
                )}
                {t.owner_user_id === null && (
                  <span className="text-[9.5px] uppercase tracking-eyebrow text-text-dim">
                    global
                  </span>
                )}
              </span>
              {selected.has(t.id) && (
                <span className="text-[10px] font-bold uppercase tracking-eyebrow text-green">on</span>
              )}
            </button>
          ))}
          {showCreateRow && (
            <button
              onClick={createAndApply}
              disabled={creating}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[12.5px] text-green hover:bg-bg-2 disabled:opacity-50"
            >
              <Plus size={14} />
              Create personal tag
              <span className="font-mono">"{filter.trim()}"</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function useFilteredTags(all: Tag[], filter: string): Tag[] {
  return useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) {
      // Sort: globals (mistake taxonomy) first, then personals alphabetically
      return [...all].sort((a, b) => {
        if (!!a.owner_user_id !== !!b.owner_user_id) return a.owner_user_id ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
    }
    return all.filter((t) => t.name.toLowerCase().includes(q));
  }, [all, filter]);
}

// ════════════════════════════════════════════════════════════════════════
// Open-position-tags + open-note buttons that go inline in a table row
// ════════════════════════════════════════════════════════════════════════
export function NoteButton({
  hasNote,
  onClick,
}: {
  hasNote: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={hasNote ? 'View note' : 'Add note'}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors',
        hasNote
          ? 'border-green/40 bg-green/[0.08] text-green hover:bg-green/[0.14]'
          : 'border-line text-text-dim hover:border-line hover:bg-bg-2 hover:text-text',
      )}
    >
      {hasNote ? <NoteIcon /> : <NoteIconOutline />}
    </button>
  );
}

export function TagButton({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={count ? `${count} tag${count === 1 ? '' : 's'}` : 'Add tags'}
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-md border px-2 transition-colors',
        count
          ? 'border-green/40 bg-green/[0.08] text-green hover:bg-green/[0.14]'
          : 'border-line text-text-dim hover:border-line hover:bg-bg-2 hover:text-text',
      )}
    >
      <TagIcon size={12} />
      <span className="text-[10px] font-semibold tabular-nums">{count}</span>
    </button>
  );
}

// Tiny inline note icons (not from lucide so they don't bloat the bundle)
function NoteIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" opacity={0.95} />
      <path d="M14 2v6h6" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}
function NoteIconOutline() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}
